import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Film, Bookmark, Menu, X, Globe, LogOut, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LANGUAGES, getLang, setGlobalLang } from "@/lib/lang";
import { beginSocialPulseLogin, getMe, logout } from "@/lib/socialpulseOidc";
import { syncWatchlistWithServer, disableWatchlistSync } from "@/lib/api";

const navLinks = [
  { to: "/explore", label: "Explore Movies" },
  { to: "/countries", label: "Countries" },
  { to: "/genres", label: "Genres" },
  { to: "/people", label: "People" },
  { to: "/festivals", label: "Festivals & Awards" },
  { to: "/news", label: "News" },
  { to: "/community", label: "Community" },
];

export const Header = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const refresh = () =>
      getMe().then((u) => {
        setUser(u);
        if (u) syncWatchlistWithServer();
        else disableWatchlistSync();
      });
    refresh();
    window.addEventListener("mp-auth-changed", refresh);
    return () => window.removeEventListener("mp-auth-changed", refresh);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/explore?q=${encodeURIComponent(q.trim())}`);
      setQ("");
      setOpen(false);
    }
  };

  const startLogin = async () => {
    try {
      const started = await beginSocialPulseLogin();
      if (!started) toast("SocialPulse sign-in isn't switched on yet — the network SSO details are still pending.");
    } catch {
      toast.error("Could not reach the SocialPulse sign-in service");
    }
  };

  const signOut = async () => {
    await logout();
    setUser(null);
    toast("Signed out. See you at the next screening!");
  };

  return (
    <header className="sticky top-0 z-50 bg-[#faf7f2]/95 backdrop-blur border-b border-[#e2d9c8]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 flex items-center gap-5 h-[68px]">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 shrink-0">
          <span className="w-10 h-10 rounded-full bg-[#7d1a1a] flex items-center justify-center border-2 border-[#5c1220]">
            <Film className="w-5 h-5 text-[#f6f1e7]" />
          </span>
          <span className="leading-none">
            <span className="font-display font-bold text-[26px] tracking-tight text-[#1c1512]">MoviePulse<span className="text-[#7d1a1a]">.pro</span></span>
            <span className="block font-mono-label text-[8px] tracking-[0.4em] uppercase text-[#a3968a] mt-1">Global Cinema Lives Here</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 ml-6">
          {navLinks.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `font-display text-[15px] font-medium link-underline pb-0.5 whitespace-nowrap ${isActive ? "text-[#7d1a1a]" : "text-[#1c1512]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 ml-auto">
          <form onSubmit={submitSearch} className="flex items-center gap-2 bg-[#efe9dc] rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-[#8a7c6d]" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search movies, people, places..."
              className="bg-transparent text-sm outline-none w-48 placeholder:text-[#a3968a]"
            />
          </form>

          <div className="relative">
            <button data-testid="header-globe-button" onClick={() => setShowLang(!showLang)} className="p-1.5 hover:text-[#7d1a1a] transition-colors" aria-label="Language">
              <Globe className="w-5 h-5" />
            </button>
            {showLang && (
              <select
                data-testid="header-language-select"
                value={getLang()}
                onChange={(e) => { setGlobalLang(e.target.value); setShowLang(false); }}
                size={10}
                className="absolute right-0 top-10 bg-[#fbf8f1] border border-[#d8ccb8] shadow-lg text-sm p-2 w-48 z-50"
              >
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
              </select>
            )}
          </div>
          <span className="text-[#d8ccb8]">|</span>
          <Link to="/watchlist" data-testid="nav-watchlist-link" className="p-1.5 hover:text-[#7d1a1a] transition-colors" aria-label="Watchlist">
            <Bookmark className="w-5 h-5" />
          </Link>
          {user ? (
            <div className="flex items-center gap-3">
              <span data-testid="header-user-name" className="flex items-center gap-1.5 font-display text-[15px] font-medium text-[#1c1512]">
                <UserCircle2 className="w-5 h-5 text-[#7d1a1a]" /> {user.name}
              </span>
              <button data-testid="nav-signout-button" onClick={signOut} className="p-1.5 hover:text-[#7d1a1a] transition-colors" aria-label="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <button data-testid="sso-socialpulse-btn" onClick={startLogin} className="font-display text-[15px] font-medium hover:text-[#7d1a1a] transition-colors">
                Sign In
              </button>
              <button data-testid="nav-join-button" onClick={startLogin} className="bg-[#7d1a1a] text-[#f6f1e7] font-display font-semibold text-[15px] px-6 py-2 rounded-md hover:bg-[#5c1220] transition-colors">
                Join
              </button>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="xl:hidden ml-auto lg:ml-0 p-2"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-[#e2d9c8] bg-[#faf7f2] px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="flex items-center gap-2 bg-[#efe9dc] rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-[#8a7c6d]" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="bg-transparent text-sm outline-none w-full"
            />
          </form>
          {navLinks.map((l) => (
            <NavLink key={l.label} to={l.to} onClick={() => setOpen(false)} className="block font-display text-base font-medium py-1">
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/watchlist" onClick={() => setOpen(false)} className="block font-display text-base font-medium py-1">
            Watchlist
          </NavLink>
        </div>
      )}
    </header>
  );
};
